import "./DealSummary.css";

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { EditGreyIcon, FullRightArrowDark } from "../../../../../assets";
import { NotesPreview } from "../../../../../templates";
import type { InteractionNote } from "../../../../../templates/NotesPanel/models";
import { CompleteTaskModal, WorkflowTaskPanel } from "../../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../../useAuth";
import {
  convertDateToStringDDMMYYYY,
  formatCurrency,
  formatNumber,
  formatPercent,
  stringKeyToSentenceCase,
} from "../../../../../utils";
import { getWorkflowTasksBoardRoute } from "../../../../../utils/routes";
import {
  Button,
  CursorDataGrid,
  DatePicker,
  DiscussionsPanel,
  ErrorBox,
  MultiSelect,
  Select,
  TextArea,
  TextInput,
} from "../../../../../widget";
import { NewTaskModal } from "../../../../shared/components/NewTaskModal";
import { DealSummaryDetailItem, DealSummaryDetailsCard, DealSummarySideCard } from "../../components";
import type { UseDealReturnData } from "../../useDeal";
import { useDealDiscussions } from "./useDealDiscussions";
import { useDealUpcomingTasks } from "./useDealUpcomingTasks";

type TeamMember = {
  key: string;
  name: string;
  role: string;
  email: string;
  avatarUrl?: string;
};

type DealSummaryTabProps = Pick<
  UseDealReturnData,
  | "deal"
  | "canEdit"
  | "isEditing"
  | "isSaving"
  | "validationErrors"
  | "dealSummaryForm"
  | "dealSourceOptions"
  | "organisationUsersSelectData"
  | "notePreviewItems"
  | "startEditing"
  | "cancelEditing"
  | "saveDealSummary"
  | "setDealSummaryFormValue"
  | "dealStructureColumns"
  | "dealStructureData"
  | "onChangeDealStructure"
> & {
  workflowUuid?: string | null;
  teamMembers: TeamMember[];
  onViewNotes: () => void;
  onViewDealStructure?: () => void;
  onApplyWorkflow?: () => void;
};

const DealSummary = ({
  deal,
  canEdit,
  isEditing,
  isSaving,
  validationErrors,
  dealSummaryForm,
  dealSourceOptions,
  organisationUsersSelectData,
  notePreviewItems,
  startEditing,
  cancelEditing,
  saveDealSummary,
  setDealSummaryFormValue,
  dealStructureColumns,
  dealStructureData,
  onChangeDealStructure,
  workflowUuid,
  teamMembers,
  onViewNotes,
  onViewDealStructure,
  onApplyWorkflow,
}: DealSummaryTabProps): JSX.Element => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();
  const {
    threads,
    permissions,
    currentUserUuid,
    hasMoreFromServer,
    hasMoreResolvedFromServer,
    onShowResolvedChange,
    onShowMore,
    onShowMoreResolved,
    onAddComment,
    onReply,
    onEditThreadMessage,
    onEditReply,
    onDeleteThread,
    onDeleteReply,
    onResolveThread,
    errors: discussionErrors,
  } = useDealDiscussions(deal?.uuid);
  const {
    columns: upcomingTasksColumns,
    defaultSortingCriteria,
    refreshTasks,
    handleRefresh,
    handleAddTask,
    isUpcomingTasksLoading,
    upcomingTasksOnChange,
    workflow,
    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    createTaskIsGeneral,
    showNewTaskModal,
    isWorkflowLoading,
    handleCloseNewTaskModal,
    handleNewTask,
    canAddTask,
    canCreateGeneralTask,
    canCreateWorkflowTask,
    selectedCompleteTask,
    showCompleteTaskModal,
    handleCloseCompleteTaskModal,
  } = useDealUpcomingTasks(workflowUuid, deal?.uuid, canEdit);

  const detailItemsLeft: DealSummaryDetailItem[] = [
    { label: "Description", value: deal?.description ?? "" },
    { label: "Deal source", value: deal?.source ?? "" },
    {
      label: "Target close date",
      value: deal?.targetCloseDate ? convertDateToStringDDMMYYYY(deal?.targetCloseDate) : "",
    },
    { label: "Estimated fees", value: formatCurrency(deal?.estimatedFees) },
    { label: "Target returns", value: formatCurrency(deal?.targetReturns) },
    {
      label: "Projected DPI",
      value: deal?.projectedDpi ? formatNumber(deal?.projectedDpi) : "",
    },
  ];

  const detailItemsRight: DealSummaryDetailItem[] = [
    { label: "Deal lead", value: deal?.leadUser?.fullName ?? "--" },
    {
      label: "Introduction date",
      value: deal?.introductionDate ? convertDateToStringDDMMYYYY(deal?.introductionDate) : "",
    },
    { label: "Investment value", value: formatCurrency(deal?.investmentValue) },
    { label: "Projected IRR (%)", value: formatPercent(deal?.projectIrr, "ratio") },
    {
      label: "Money multiple",
      value: deal?.moneyMultiple ? formatNumber(deal?.moneyMultiple) : "",
    },
    {
      label: "Conversion probability (%)",
      value:
        deal?.conversionProbability !== null && deal?.conversionProbability !== undefined
          ? formatPercent(deal?.conversionProbability, "ratio")
          : "",
    },
  ];

  const hasTeamMembers = teamMembers.length > 0;
  const hasWorkflowApplied = Boolean(workflowUuid);
  const notesPreviewItems = useMemo<InteractionNote[]>(() => notePreviewItems ?? [], [notePreviewItems]);
  const dealTeamSelectData = useMemo(
    () => organisationUsersSelectData.filter((user) => user.key !== dealSummaryForm.dealLeadUuid),
    [dealSummaryForm.dealLeadUuid, organisationUsersSelectData]
  );
  const dealTeamValues = useMemo(
    () => dealSummaryForm.dealTeamUuids.filter((uuid) => uuid !== dealSummaryForm.dealLeadUuid),
    [dealSummaryForm.dealLeadUuid, dealSummaryForm.dealTeamUuids]
  );
  const workflowStageOptions = useMemo(
    () =>
      workflow?.phases?.map((phase) => ({
        key: phase.phase,
        value: stringKeyToSentenceCase(phase.phase),
      })) ?? [],
    [workflow?.phases]
  );

  const handleDealTeamChange = (values: string[]): void => {
    const nextValues = dealSummaryForm.dealLeadUuid ? [...values, dealSummaryForm.dealLeadUuid] : values;
    setDealSummaryFormValue("dealTeamUuids", Array.from(new Set(nextValues)));
  };

  return (
    <>
      <div className="DealSummaryGrid">
        <div className="DealSummaryMain">
          {isEditing ? (
            <div className="DealSummaryEditCard">
              <div className="DealSummaryEditHeader">
                <h2>Deal summary</h2>
              </div>
              {validationErrors.length > 0 && (
                <ErrorBox
                  title="Please solve the below issues before saving"
                  text={validationErrors.map((error) => error.message)}
                />
              )}
              <div className="DealSummaryEditForm">
                <div className="DealSummaryEditGrid">
                  <TextInput
                    label="Deal name"
                    name="dealName"
                    value={dealSummaryForm.name}
                    onChange={(value) => setDealSummaryFormValue("name", value)}
                  />
                </div>
                <TextArea
                  name="dealDescription"
                  label="Description"
                  placeholder="Enter a description..."
                  value={dealSummaryForm.description}
                  onChange={(value) => setDealSummaryFormValue("description", value)}
                  corners="rounded"
                />
                <div className="DealSummaryEditGrid">
                  <Select
                    label="Deal lead"
                    data={organisationUsersSelectData}
                    value={dealSummaryForm.dealLeadUuid}
                    onChange={(value) => setDealSummaryFormValue("dealLeadUuid", value)}
                    corners="rounded"
                  />
                  <MultiSelect
                    label="Deal team"
                    data={dealTeamSelectData}
                    values={dealTeamValues}
                    onChange={handleDealTeamChange}
                  />
                  <Select
                    label="Deal source"
                    data={dealSourceOptions}
                    value={dealSummaryForm.dealSource}
                    onChange={(value) => setDealSummaryFormValue("dealSource", value)}
                    corners="rounded"
                  />
                  <DatePicker
                    label="Introduction date"
                    value={dealSummaryForm.introductionDate}
                    onChange={(value) => setDealSummaryFormValue("introductionDate", value)}
                    corners="rounded"
                  />
                  <DatePicker
                    label="Target close date"
                    value={dealSummaryForm.targetCloseDate}
                    onChange={(value) => setDealSummaryFormValue("targetCloseDate", value)}
                    corners="rounded"
                  />
                  <TextInput
                    label="Estimated fees"
                    name="estimatedFees"
                    type="number"
                    value={dealSummaryForm.estimatedFees}
                    onChange={(value) => setDealSummaryFormValue("estimatedFees", value)}
                  />
                  <TextInput
                    label="Projected DPI"
                    name="projectDpi"
                    type="number"
                    value={dealSummaryForm.projectDpi}
                    onChange={(value) => setDealSummaryFormValue("projectDpi", value)}
                  />
                  <TextInput
                    label="Conversion probability (%)"
                    name="conversionProbability"
                    type="number"
                    value={dealSummaryForm.conversionProbability}
                    onChange={(value) => setDealSummaryFormValue("conversionProbability", value)}
                    maxNumberValue={100}
                    minNumberValue={0}
                    decimalPlaces={2}
                  />
                </div>
              </div>
              <div className="DealSummaryEditActions">
                <Button
                  text="Save changes"
                  variant="primary"
                  size="small"
                  corners="rounded"
                  isLoading={isSaving}
                  onClick={saveDealSummary}
                />
                <Button text="Cancel" variant="quaternary" size="small" corners="rounded" onClick={cancelEditing} />
              </div>
            </div>
          ) : (
            <DealSummaryDetailsCard
              title="Deal summary"
              leftItems={detailItemsLeft}
              rightItems={detailItemsRight}
              action={
                canEdit && (
                  <button
                    type="button"
                    className="DealSummaryEditButton"
                    onClick={startEditing}
                    aria-label="Edit deal summary"
                  >
                    <EditGreyIcon />
                  </button>
                )
              }
            />
          )}
          <div className="DealSummaryDealStructure">
            <div className="DealSummaryDealStructureHeader">
              <h2>Deal structure</h2>
              {onViewDealStructure && (
                <button type="button" className="DealSummaryDealStructureLink" onClick={onViewDealStructure}>
                  <span className="body2">View breakdown</span>
                </button>
              )}
            </div>
            <div className="DealSummaryDealStructureRows">
              <CursorDataGrid
                columns={dealStructureColumns}
                data={dealStructureData}
                clientSide
                onChange={onChangeDealStructure}
                pageable={false}
                showTotalCount={false}
                sortable={false}
                filterable={false}
                hasNoPadding
                hasBottomMargin={false}
                noDataMessage="No deal structure data to display"
                variant="seed"
                pagination={{ pageSize: 50 }}
              />
            </div>
          </div>
          <div className="DealSummaryUpcomingTasks">
            <div className="DealSummaryUpcomingTasksHeader">
              <h2>Upcoming tasks</h2>
              <div className="DealSummaryUpcomingTasksActions">
                {canAddTask && (
                  <Button
                    className="DealSummaryUpcomingTasksButton"
                    text="Add task"
                    variant="primary"
                    size="small"
                    corners="rounded"
                    minWidth={false}
                    onClick={handleAddTask}
                  />
                )}
                {/* The board and the apply-workflow link are the workflow's own, unlike a general task. */}
                {hasWorkflowApplied ? (
                  <Button
                    className="DealSummaryUpcomingTasksButton"
                    text="Task board"
                    variant="secondary"
                    size="small"
                    corners="rounded"
                    icon={<FullRightArrowDark />}
                    minWidth={false}
                    onClick={() => {
                      if (workflowUuid) {
                        navigate(getWorkflowTasksBoardRoute(workflowUuid, currentUserType));
                      }
                    }}
                  />
                ) : (
                  canEdit && (
                    <button type="button" className="DealSummaryDealStructureLink" onClick={onApplyWorkflow}>
                      <span className="body2">Apply a workflow</span>
                      <FullRightArrowDark />
                    </button>
                  )
                )}
              </div>
            </div>
            {/* Rendered whether or not a workflow is applied — general tasks hang off the deal itself. */}
            <CursorDataGrid
              columns={upcomingTasksColumns}
              data={[]}
              clientSide={false}
              onChange={upcomingTasksOnChange}
              pageable={false}
              showTotalCount={false}
              sortable={false}
              filterable={false}
              hasNoPadding
              hasBottomMargin={false}
              noDataMessage="You have completed all the tasks associated with this deal. You can add tasks by creating them manually or by modifying the applied workflow."
              variant="seed"
              dataIsLoading={isUpcomingTasksLoading}
              refresh={refreshTasks}
              defaultSortingCriteria={defaultSortingCriteria}
              pagination={{ pageSize: 5 }}
            />
          </div>
          <div className="DealSummaryDiscussions">
            <DiscussionsPanel
              threads={threads}
              permissions={permissions}
              currentUserUuid={currentUserUuid}
              variant="seed"
              font="figtree"
              hasMoreFromServer={hasMoreFromServer}
              hasMoreResolvedFromServer={hasMoreResolvedFromServer}
              onShowResolvedChange={onShowResolvedChange}
              onShowMore={onShowMore}
              onShowMoreResolved={onShowMoreResolved}
              onAddComment={onAddComment}
              onReply={onReply}
              onEditThreadMessage={onEditThreadMessage}
              onEditReply={onEditReply}
              onDeleteThread={onDeleteThread}
              onDeleteReply={onDeleteReply}
              onResolveThread={onResolveThread}
              errors={discussionErrors}
            />
          </div>
        </div>
        <div className="DealSummarySide">
          <DealSummarySideCard
            title="Notes"
            action={
              <button type="button" className="DealSummaryNotesLink body2" onClick={onViewNotes}>
                View all
              </button>
            }
          >
            <NotesPreview notes={notesPreviewItems} maxItems={3} emptyText="No notes yet" />
          </DealSummarySideCard>
          <DealSummarySideCard title="Team involved">
            {!hasTeamMembers ? (
              <p className="body2Light">--</p>
            ) : (
              <div className="DealSummaryTeamList">
                {teamMembers.map((member) => (
                  <div key={member.key} className="DealSummaryTeamMember">
                    <div className="DealSummaryTeamAvatar">
                      {member.avatarUrl ? (
                        <img src={member.avatarUrl} alt={member.name} />
                      ) : (
                        <span className="DealSummaryAvatarFallback">{member.name?.[0]?.toUpperCase()}</span>
                      )}
                    </div>
                    <div className="DealSummaryTeamInfo">
                      <p className="body1">{member.name}</p>
                      <p className="body2Light">{member.role}</p>
                      {member.email && <p className="body2Light">{member.email}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DealSummarySideCard>
        </div>
      </div>
      <NewTaskModal
        show={showNewTaskModal}
        canCreateWorkflowTask={canCreateWorkflowTask}
        canCreateGeneralTask={canCreateGeneralTask}
        isLoading={isWorkflowLoading}
        description="Create a new task for this deal"
        onCancel={handleCloseNewTaskModal}
        onConfirm={handleNewTask}
      />
      <WorkflowTaskPanel
        task={selectedPanelTask}
        currentStepUuid={selectedPanelTask?.step?.uuid}
        workflow={workflow}
        /*
          The type chosen for the task being created: a general task is standalone and hangs off this deal,
          a workflow task takes a step from the applied workflow. Opening an existing row still edits
          whatever it is — the panel reads the type off the task itself.
        */
        isGeneralTask={createTaskIsGeneral}
        parentName={deal?.name}
        parentObjectType="Deal"
        parentObjectUuid={deal?.uuid}
        show={showWorkflowTaskPanel}
        phaseOptions={workflowStageOptions}
        phaseLabel="Stage"
        isRepeatablePhase={() => false}
        refresh={refreshTasks}
        handleRefresh={handleRefresh}
        onClose={handleCloseWorkflowTaskPanel}
      />
      {selectedCompleteTask && (
        <CompleteTaskModal
          task={selectedCompleteTask}
          show={showCompleteTaskModal}
          closeModal={handleCloseCompleteTaskModal}
          closePanel={handleCloseCompleteTaskModal}
          handleRefresh={handleRefresh}
        />
      )}
    </>
  );
};

export default DealSummary;
